import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";

import {
  UIButton,
  UIFormSectionCard,
  UIRating,
  UIStatusTag,
  UITextarea,
} from "../../../ui";

import "./_Evaluations.scss";

const buildInitialRatings = (sections) =>
  sections
    .flatMap((s) => s.criteriaKeys)
    .reduce((acc, key) => ({ ...acc, [key]: 0 }), {});

// Presentational rating core shared across portals: a set of criteria sections,
// a feedback field, and a submit/cancel footer. Owns ratings + feedback state and
// surfaces them on submit. Knows nothing about breadcrumbs, page titles, applicant
// cards, or portal-specific copy — wrappers supply all strings and surrounding chrome.
const RatingForm = ({
  sections,
  criteriaLabels,
  layout,
  feedbackTitle,
  feedbackPlaceholder,
  statusSectionLabel,
  statusLabel,
  submitLabel,
  cancelLabel,
  onSubmit,
  onCancel,
}) => {
  const [ratings, setRatings] = useState(() => buildInitialRatings(sections));
  const [feedback, setFeedback] = useState("");

  const allCriteriaKeys = sections.flatMap((s) => s.criteriaKeys);
  const isSubmitDisabled = allCriteriaKeys.some((key) => !ratings[key]);

  const handleRatingChange = (key, value) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    onSubmit({ ratings, feedback });
  };

  const renderCriteriaItem = (key) => (
    <Box key={key} className="eval-applicant-form__criteria-item">
      <Typography className="eval-applicant-form__criteria-label">
        {criteriaLabels?.[key] ?? key}
      </Typography>
      <UIRating value={ratings[key]} onChange={(val) => handleRatingChange(key, val)} />
    </Box>
  );

  return (
    <>
      {layout === "grid" ? (
        sections.map((section) => (
          <UIFormSectionCard
            key={section.key}
            title={section.title}
            action={section.action}
          >
            <Box className="eval-applicant-form__criteria-grid">
              {section.criteriaKeys.map(renderCriteriaItem)}
            </Box>
          </UIFormSectionCard>
        ))
      ) : (
        <Box sx={{ display: "flex", gap: "var(--space-16)", alignItems: "stretch" }}>
          {sections.map((section) => (
            <UIFormSectionCard key={section.key} title={section.title} sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "var(--space-16)" }}>
                {section.criteriaKeys.map(renderCriteriaItem)}
              </Box>
            </UIFormSectionCard>
          ))}
        </Box>
      )}

      <UIFormSectionCard title={feedbackTitle}>
        <UITextarea
          placeholder={feedbackPlaceholder}
          value={feedback}
          onValueChange={setFeedback}
          fullWidth
          minRows={5}
        />
      </UIFormSectionCard>

      <UIFormSectionCard>
        <Box className="eval-applicant-form__footer">
          <Box className="eval-applicant-form__footer-status">
            <Typography className="eval-applicant-form__footer-status-label">
              {statusSectionLabel}
            </Typography>
            <UIStatusTag
              status="in_progress"
              label={statusLabel}
              tone="warningSoft"
              preset="table"
            />
          </Box>
          <Box className="eval-applicant-form__footer-actions">
            <UIButton variant="outline" size="medium" onClick={onCancel}>
              {cancelLabel}
            </UIButton>
            <UIButton
              appearance="gradient"
              size="medium"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              {submitLabel}
            </UIButton>
          </Box>
        </Box>
      </UIFormSectionCard>
    </>
  );
};

RatingForm.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string,
      criteriaKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
      action: PropTypes.node,
    }),
  ).isRequired,
  criteriaLabels: PropTypes.objectOf(PropTypes.string),
  layout: PropTypes.oneOf(["columns", "grid"]),
  feedbackTitle: PropTypes.string,
  feedbackPlaceholder: PropTypes.string,
  statusSectionLabel: PropTypes.string,
  statusLabel: PropTypes.string,
  submitLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

RatingForm.defaultProps = {
  criteriaLabels: null,
  layout: "columns",
  feedbackTitle: undefined,
  feedbackPlaceholder: undefined,
  statusSectionLabel: undefined,
  statusLabel: undefined,
  submitLabel: undefined,
  cancelLabel: undefined,
};

export default RatingForm;
